import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../stores/useAppStore';
import { setTokenCookie } from '../lib/authToken';
import { maybeAutoBootstrap } from '../lib/bootstrap';
import './TokenPrompt.css';

type Status =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'err'; message: string };

function defaultBaseUrl(): string {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
  return window.location.origin + base + '/api';
}

async function verifyToken(token: string, baseUrlOverride: string): Promise<string> {
  const base = (baseUrlOverride.trim().replace(/\/+$/, '')) || defaultBaseUrl();
  let resp: Response;
  try {
    resp = await fetch(base + '/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    throw new Error(`Network error: ${(e as Error).message}`);
  }
  if (resp.status === 401) throw new Error('Token rejected by server.');
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = (await resp.json()) as { user?: string };
  return data.user ?? '';
}

export default function TokenPrompt() {
  const apiToken = useAppStore((s) => s.settings.apiToken);
  const apiBaseUrl = useAppStore((s) => s.settings.apiBaseUrl);
  const setSettings = useAppStore((s) => s.setSettings);

  const [draftToken, setDraftToken] = useState('');
  const [draftBaseUrl, setDraftBaseUrl] = useState(apiBaseUrl ?? '');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!apiToken) inputRef.current?.focus();
  }, [apiToken]);

  if (apiToken) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = draftToken.trim();
    if (!token) return;
    setStatus({ kind: 'busy' });
    try {
      await verifyToken(token, draftBaseUrl);
      setTokenCookie(token);
      setSettings({
        apiToken: token,
        apiBaseUrl: draftBaseUrl.trim() || undefined,
      });
      // Re-arm the bootstrap decision now that we have a token, then run it.
      useAppStore.getState().setBootstrapStatus('pending');
      void maybeAutoBootstrap();
    } catch (err) {
      setStatus({
        kind: 'err',
        message: (err as Error).message || 'Failed to verify token.',
      });
    }
  };

  return (
    <div className="token-prompt-backdrop">
      <form className="token-prompt card" onSubmit={submit}>
        <h2>Sign in</h2>
        <div className="dim small">
          Paste your sync token to connect this device. The token is saved to a
          cookie on this device only.
        </div>
        <label>
          <div className="label">Sync token</div>
          <input
            ref={inputRef}
            type="password"
            value={draftToken}
            onChange={(e) => setDraftToken(e.target.value)}
            autoComplete="off"
            placeholder="tok_…"
            disabled={status.kind === 'busy'}
          />
        </label>
        <details>
          <summary className="dim small">Advanced: custom API URL</summary>
          <label style={{ marginTop: 8 }}>
            <input
              type="text"
              value={draftBaseUrl}
              onChange={(e) => setDraftBaseUrl(e.target.value)}
              autoComplete="off"
              placeholder="(default: same origin + /api)"
              disabled={status.kind === 'busy'}
            />
          </label>
        </details>
        {status.kind === 'err' && (
          <div className="token-prompt-err small">{status.message}</div>
        )}
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            type="submit"
            className="primary"
            disabled={!draftToken.trim() || status.kind === 'busy'}
          >
            {status.kind === 'busy' ? 'Verifying…' : 'Connect'}
          </button>
        </div>
      </form>
    </div>
  );
}
